/**
 * FilterPresetsDropdown — Compact popover for saved filter presets.
 *
 * Shows a bookmark icon button that opens a dropdown with:
 *  - List of saved presets (click to apply, × to delete)
 *  - "Save current filters…" inline input
 */

import { useState, useRef, useEffect } from 'react';
import { Bookmark, X, Plus } from 'lucide-react';
import type { SavedFilterPreset } from '../../utils/filterStorage';

interface FilterPresetsDropdownProps {
  presets: SavedFilterPreset[];
  hasActiveFilters: boolean;
  onSave: (name: string) => void;
  onApply: (id: string) => void;
  onDelete: (id: string) => void;
  /** Compact variant for tight filter rows (smaller button) */
  compact?: boolean;
}

export function FilterPresetsDropdown({
  presets,
  hasActiveFilters,
  onSave,
  onApply,
  onDelete,
  compact = false,
}: FilterPresetsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the input when entering save mode
  useEffect(() => {
    if (saving) inputRef.current?.focus();
  }, [saving]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSaving(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSave = () => {
    const trimmed = presetName.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setPresetName('');
    setSaving(false);
  };

  const buttonSize = compact ? 'p-1.5' : 'px-2.5 py-1.5';
  const iconSize = compact ? 13 : 14;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`${buttonSize} rounded-md text-xs transition-colors flex items-center gap-1
          ${open
            ? 'bg-coral-50 text-coral-600'
            : presets.length > 0
              ? 'bg-surface-muted text-muted hover:bg-surface-subtle'
              : 'text-subtle hover:text-muted hover:bg-surface-muted'
          }`}
        title={presets.length > 0 ? `${presets.length} saved filter${presets.length !== 1 ? 's' : ''}` : 'Save filters'}
      >
        <Bookmark size={iconSize} />
        {!compact && presets.length > 0 && (
          <span className="text-[10px] font-medium">{presets.length}</span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-line z-30 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 border-b border-line">
            <span className="text-xs font-semibold text-muted">Saved Filters</span>
          </div>

          {/* Preset list */}
          {presets.length > 0 ? (
            <div className="max-h-48 overflow-y-auto">
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-surface-muted transition-colors group"
                >
                  <button
                    onClick={() => {
                      onApply(preset.id);
                      setOpen(false);
                    }}
                    className="flex-1 text-left text-xs text-text truncate"
                  >
                    {preset.name}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(preset.id);
                    }}
                    className="ml-2 p-0.5 rounded text-subtle hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete preset"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-3 py-4 text-center text-xs text-subtle">
              No saved filters yet
            </div>
          )}

          {/* Save current */}
          <div className="border-t border-line px-3 py-2">
            {saving ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
                className="flex items-center gap-1.5"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Preset name…"
                  className="flex-1 text-xs px-2 py-1 rounded border border-line
                    focus:ring-1 focus:ring-coral-200 focus:border-coral-300 outline-none"
                  maxLength={40}
                />
                <button
                  type="submit"
                  disabled={!presetName.trim()}
                  className="px-2 py-1 text-xs font-medium rounded bg-coral-500 text-white
                    hover:bg-coral-600 disabled:opacity-40 transition-colors"
                >
                  Save
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSaving(true)}
                disabled={!hasActiveFilters}
                className="w-full flex items-center gap-1.5 text-xs text-muted
                  hover:text-coral-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} />
                Save current filters…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
